/**
 * Generate Mermaid flowchart from PIPELINE_FLOW — STEP-LEVEL nodes.
 *
 * Phases become subgraphs containing all their steps. Sequential steps are
 * connected by default edges. Branching steps (Phase 2 Step 7, Phase 7 Step 3)
 * get explicit labeled edges to their target steps in other phases.
 *
 * Quality-Gate: scans for unreachable steps, edges without labels, dead-ends.
 */
import { PIPELINE_FLOW, type PhaseSlug, type StepTransition } from "./pipelineFlow";
import type { Card, Timesheet } from "./api";

export interface MermaidRender {
  source: string;
  findings: string[];
  pathTaken: string[];   // step-node-ids
  terminal: "END" | null;
}

function stepId(phase: PhaseSlug, step: number): string {
  return `${phase}_s${step}`;
}

function escapeLabel(s: string): string {
  return s.replace(/[|"<>(){}]/g, " ").replace(/\s+/g, " ").trim();
}

function inferPath(card: Card | null, ts: Timesheet | null): { path: string[]; terminal: "END" | null } {
  if (!card) return { path: [], terminal: null };
  const path: string[] = [];
  const data = (ts?.data as any) ?? {};
  const days = data.days ?? {};
  const hasOcr = Object.keys(days).some(k => /^\d+$/.test(k));
  const hadConflict = card.state === "pending_conflict" || !!data.conflict_resolved;
  const resolvedAction = data.conflict_resolved?.action as string | undefined;

  // Phase 1: always visited
  for (let i = 1; i <= 3; i++) path.push(stepId("upload", i));

  if (card.state === "uploaded" || card.state === "pending_review") {
    return { path, terminal: null };
  }

  // Phase 2: visited if OCR ran or state past it
  for (let i = 1; i <= 7; i++) path.push(stepId("ocr", i));

  if (hadConflict) {
    // Phase 7 steps
    path.push(stepId("konflikt", 1));
    path.push(stepId("konflikt", 2));
    path.push(stepId("konflikt", 3));
    if (resolvedAction === "discard" || card.state === "archived") {
      return { path, terminal: "END" };
    }
    if (resolvedAction === "overwrite" && card.state === "superseded") {
      return { path, terminal: "END" };
    }
    // overwrite/reassign — continue to validation
  }

  if (hasOcr && (["validated","calculated","exported"].includes(card.state))) {
    for (let i = 1; i <= 4; i++) path.push(stepId("validierung", i));
  }
  if (["calculated","exported"].includes(card.state)) {
    for (let i = 1; i <= 9; i++) path.push(stepId("berechnung", i));
    for (let i = 1; i <= 4; i++) path.push(stepId("review", i));
  }
  if (card.state === "exported") {
    for (let i = 1; i <= 4; i++) path.push(stepId("export", i));
    return { path, terminal: "END" };
  }
  if (["archived","superseded"].includes(card.state)) {
    return { path, terminal: "END" };
  }
  return { path, terminal: null };
}

export function generateMermaid(card: Card | null, ts: Timesheet | null, currentPhase: PhaseSlug | null): MermaidRender {
  const lines: string[] = [];
  const findings: string[] = [];
  const { path, terminal } = inferPath(card, ts);
  const pathSet = new Set(path);

  lines.push("flowchart TD");

  // START + entry
  lines.push("  START((Start))");
  lines.push(`  START --> ${stepId("upload", 1)}`);

  // Subgraph per phase with all steps
  for (const flow of PIPELINE_FLOW) {
    if (flow.steps.length === 0) {
      findings.push(`Phase ${flow.slug} hat keine Steps`);
      continue;
    }
    lines.push(`  subgraph ${flow.slug}_phase ["${flow.num}. ${escapeLabel(flow.title)}"]`);
    lines.push("    direction TB");
    for (const step of flow.steps) {
      const id = stepId(flow.slug, step.num);
      const label = `${flow.num}.${step.num} ${escapeLabel(step.label)}`;
      // Branching steps as diamonds (rhombus), user-gates as stadium, default rectangle
      const shape = step.branches?.length ? `{"${label}"}` : step.userGate ? `(["${label}"])` : `["${label}"]`;
      lines.push(`    ${id}${shape}`);
    }
    // Sequential edges within phase
    for (let i = 0; i < flow.steps.length - 1; i++) {
      const cur = flow.steps[i]!;
      const next = flow.steps[i + 1]!;
      // If current step has its own branches, those override sequential
      if (cur.branches?.length) continue;
      lines.push(`    ${stepId(flow.slug, cur.num)} --> ${stepId(flow.slug, next.num)}`);
    }
    lines.push("  end");
  }

  // END node
  lines.push("  END((Ende))");

  // Branching edges (from steps with branches[]) + phase-level endTransitions
  for (const flow of PIPELINE_FLOW) {
    for (const step of flow.steps) {
      if (!step.branches?.length) continue;
      for (const t of step.branches) {
        renderTransition(lines, findings, stepId(flow.slug, step.num), t);
      }
    }
    // Phase-end transitions on the LAST step (if it has no own branches)
    const last = flow.steps[flow.steps.length - 1];
    if (last && !last.branches?.length && flow.endTransitions.length) {
      for (const t of flow.endTransitions) {
        renderTransition(lines, findings, stepId(flow.slug, last.num), t);
      }
    }
  }

  // Style — classDef for highlighting
  lines.push("");
  lines.push("  classDef current fill:#1d4ed8,stroke:#1e3a8a,color:#fff,stroke-width:2px;");
  lines.push("  classDef taken fill:#10b981,stroke:#047857,color:#fff;");
  lines.push("  classDef terminal fill:#6b7280,stroke:#374151,color:#fff;");
  lines.push("  classDef notReached fill:#f3f4f6,stroke:#9ca3af,color:#6b7280;");

  // Mark taken steps green
  if (path.length > 0) {
    lines.push(`  class ${path.join(",")} taken;`);
  }
  // Mark current-phase steps blue (override taken)
  if (currentPhase) {
    const flow = PIPELINE_FLOW.find(f => f.slug === currentPhase);
    if (flow) {
      const ids = flow.steps.map(s => stepId(currentPhase, s.num));
      lines.push(`  class ${ids.join(",")} current;`);
    }
  }
  // Not-reached: all steps not in path and not in current phase
  const notReached: string[] = [];
  for (const flow of PIPELINE_FLOW) {
    if (flow.slug === currentPhase) continue;
    for (const step of flow.steps) {
      const id = stepId(flow.slug, step.num);
      if (!pathSet.has(id)) notReached.push(id);
    }
  }
  if (notReached.length > 0) {
    lines.push(`  class ${notReached.join(",")} notReached;`);
  }
  if (terminal === "END") {
    lines.push("  class END terminal;");
  }

  // Sanity scan
  const allStepIds = new Set<string>();
  for (const flow of PIPELINE_FLOW) {
    for (const step of flow.steps) allStepIds.add(stepId(flow.slug, step.num));
  }
  const reachedIds = new Set<string>([stepId("upload", 1)]);
  for (const flow of PIPELINE_FLOW) {
    for (let i = 0; i < flow.steps.length - 1; i++) {
      const cur = flow.steps[i]!;
      if (!cur.branches?.length) {
        reachedIds.add(stepId(flow.slug, flow.steps[i + 1]!.num));
      }
    }
    for (const step of flow.steps) {
      const out = step.branches?.length ? step.branches : (step === flow.steps[flow.steps.length - 1] ? flow.endTransitions : []);
      for (const t of out) {
        if (t.to.kind === "step") reachedIds.add(stepId(t.to.phase, t.to.step));
      }
    }
  }
  for (const id of allStepIds) {
    if (!reachedIds.has(id)) findings.push(`Step ${id} ist unreachable (keine eingehende Edge)`);
  }

  return { source: lines.join("\n"), findings, pathTaken: path, terminal };
}

function renderTransition(lines: string[], findings: string[], fromId: string, t: StepTransition): void {
  const lbl = escapeLabel(t.label);
  if (!lbl) findings.push(`Edge from ${fromId} hat kein Label`);
  let target: string;
  if (t.to.kind === "end") target = "END";
  else if (t.to.kind === "wait") return; // wait is not a graph edge
  else if (t.to.kind === "step") target = stepId(t.to.phase, t.to.step);
  else target = "END";
  lines.push(`  ${fromId} -- "${lbl}" --> ${target}`);
}
